import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import * as Sentry from "@sentry/angular";

const PRESIGNED_QUERY_PATTERN = /\?[^\s"'#]*X-Amz-[^\s"'#]*/g;

function scrubPresignedUrls(value: string): string {
  return value.replace(PRESIGNED_QUERY_PATTERN, '');
}

function scrubStringValues(data: { [key: string]: unknown } | undefined): void {
  if (!data) {
    return;
  }
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      data[key] = scrubPresignedUrls(value);
    }
  }
}

if (environment.production) {
  enableProdMode();
}

// Independent of production mode: a build can be production-optimized (dev, preprod,
// prod, internal all are) without wanting Sentry's overhead — local and dev traffic
// isn't real user sessions, so it stays off there. Rates are per-environment (see each
// environment.*.ts) rather than one blanket value, since preprod/prod/internal carry
// very different traffic volumes and cost tolerances.
if (environment.sentry.enabled) {
  Sentry.init({
    dsn: "https://d7bf8c278f0d1debd2036d8d812da357@o4504282426114048.ingest.us.sentry.io/4507932188475392",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        beforeAddRecordingEvent: (event) => {
          const data = event.data;
          if (data.tag === 'performanceSpan') {
            data.payload.description = scrubPresignedUrls(
              data.payload.description,
            );
          }
          return event;
        },
      }),
    ],
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.message) {
        breadcrumb.message = scrubPresignedUrls(breadcrumb.message);
      }
      scrubStringValues(breadcrumb.data);
      return breadcrumb;
    },
    beforeSendSpan: (span) => {
      if (span.description) {
        span.description = scrubPresignedUrls(span.description);
      }
      scrubStringValues(span.data);
      return span;
    },
    beforeSendTransaction: (event) => {
      scrubStringValues(event.contexts?.trace?.data);
      return event;
    },
    // Tracing
    tracesSampleRate: environment.sentry.tracesSampleRate,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // Session Replay
    replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
    // profilesSampleRate is relative to tracesSampleRate: the final profiled fraction
    // of all transactions is tracesSampleRate * profilesSampleRate.
    profilesSampleRate: environment.sentry.profilesSampleRate,
  });
}

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err));
